"use client";

import { useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useTranslations } from "next-intl";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Description, DialogTitle } from "@radix-ui/react-dialog";
import { Typography } from "@/components/custom/typography";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTermOfService } from "@/hooks/sign-up-hooks/useTermOfService";

export function AgreeNotification({
  open,
  onClose,
  onAgree,
  defaultCheckedEmail = false,
  defaultCheckedSMS = false,
}: {
  open: boolean;
  onClose: () => void;
  onAgree: (values: { agreeEmail: boolean; agreeSMS: boolean }) => void;
  defaultCheckedEmail?: boolean;
  defaultCheckedSMS?: boolean;
}) {
  type FormData = {
    agreeEmail: boolean;
    agreeSMS: boolean;
  };

  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      agreeEmail: defaultCheckedEmail,
      agreeSMS: defaultCheckedSMS,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        agreeEmail: defaultCheckedEmail,
        agreeSMS: defaultCheckedSMS,
      });
    }
  }, [open, defaultCheckedEmail, defaultCheckedSMS, reset]);

  const emailAgree = useWatch({ control, name: "agreeEmail" });
  const smsAgree = useWatch({ control, name: "agreeSMS" });
  const canSubmit = emailAgree || smsAgree;

  const { data } = useTermOfService();
  const t = useTranslations("dialogs.notificationConsent");

  const advertisingPolicy = data?.find(
    (item) => item.policy_type === "marketing",
  );

  if (!advertisingPolicy) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!max-w-4xl p-10 flex flex-col gap-9 [&>button]:hidden">
        {/* Title & Description */}
        <div>
          <DialogTitle></DialogTitle>
          <Typography variant="subheading1" tone="strong" className="mb-3">
            {t("title")}
          </Typography>
          <Description>
            <Typography as="span" variant="body2">
              {t("description")}
            </Typography>
          </Description>
        </div>
        {/* Checkbox section */}
        <div className="space-y-5">
          {}
          <ScrollArea className="h-64">
            <div className="p-2.5 rounded bg-gray-200 whitespace-pre-line">
              <Typography as="div" variant="body4" className="text-black">
                {advertisingPolicy?.policy_content ??
                  "약관 내용을 불러오는 중입니다..."}
              </Typography>
            </div>
          </ScrollArea>
          <Typography variant="subheading3" className="mb-3 font-semibold">
            {t("channelTitle")}
          </Typography>
          <div className="flex items-center gap-3 bg-muted p-1 rounded-xs w-full">
            <Controller
              control={control}
              name="agreeEmail"
              render={({ field }) => (
                <div className="flex items-center  gap-1 ">
                  <Checkbox
                    id="agree-email"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <Typography as="span" variant={"body3"}>
                    {t("emailLabel")}
                  </Typography>
                </div>
              )}
            />
            <Controller
              control={control}
              name="agreeSMS"
              render={({ field }) => (
                <div className="flex items-center  gap-1 ">
                  <Checkbox
                    id="agree-sms"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <Typography as="span" variant="body3">
                    {t("smsLabel")}
                  </Typography>
                </div>
              )}
            />
          </div>
        </div>
        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            tone={"light"}
            onClick={onClose}
            className="border border-gray-300 font-semibold text-base"
          >
            {t("cancelText")}
          </Button>
          <Button
            type="submit"
            className="font-semibold text-base"
            onClick={handleSubmit((data) => {
              onAgree(data);
              onClose();
            })}
            disabled={!canSubmit}
          >
            {t("okText")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
